import { useQuery } from "@tanstack/react-query";

import {
  searchAppointments,
  searchClients,
  searchFamilies,
  searchPatients,
  searchProducts,
  searchSales,
} from "./api";
import type { SearchGroup, SearchScopes } from "./types";

const GROUP_LABEL: Record<SearchGroup["type"], string> = {
  patient: "Patients",
  client: "Clients",
  family: "Familles",
  sale: "Factures",
  product: "Produits",
  appointment: "Rendez-vous",
};

/** Longueur minimale avant de déclencher une recherche. */
export const SEARCH_MIN_CHARS = 2;

/**
 * Recherche globale débouncée (le débounce est géré par l'appelant qui passe
 * `query` déjà stabilisé). Interroge en parallèle les seules ressources
 * autorisées et regroupe les résultats.
 */
export function useGlobalSearch(query: string, scopes: SearchScopes) {
  const q = query.trim();
  const enabledKey = [
    scopes.patient,
    scopes.client,
    scopes.family,
    scopes.sale,
    scopes.product,
    scopes.appointment,
  ].join("-");

  return useQuery({
    queryKey: ["global-search", q, enabledKey],
    enabled: q.length >= SEARCH_MIN_CHARS,
    staleTime: 30_000,
    queryFn: async (): Promise<SearchGroup[]> => {
      const tasks: Array<Promise<SearchGroup>> = [];
      if (scopes.patient) {
        tasks.push(searchPatients(q).then((items) => ({ type: "patient", label: GROUP_LABEL.patient, items })));
      }
      if (scopes.client) {
        tasks.push(searchClients(q).then((items) => ({ type: "client", label: GROUP_LABEL.client, items })));
      }
      if (scopes.family) {
        tasks.push(searchFamilies(q).then((items) => ({ type: "family", label: GROUP_LABEL.family, items })));
      }
      if (scopes.appointment) {
        tasks.push(
          searchAppointments(q).then((items) => ({ type: "appointment", label: GROUP_LABEL.appointment, items })),
        );
      }
      if (scopes.sale) {
        tasks.push(searchSales(q).then((items) => ({ type: "sale", label: GROUP_LABEL.sale, items })));
      }
      if (scopes.product) {
        tasks.push(searchProducts(q).then((items) => ({ type: "product", label: GROUP_LABEL.product, items })));
      }
      const groups = await Promise.all(tasks);
      return groups.filter((g) => g.items.length > 0);
    },
  });
}
